import { inject, Injectable, signal } from '@angular/core';
import { catchError, filter, finalize, map, Observable, of, shareReplay, tap } from 'rxjs';

import { City } from '@app/core/types/city.interface';

import { AppSettingApiService } from './app-setting-api.service';

@Injectable({
  providedIn: 'root',
})
export class AppCityService {
  private readonly api = inject(AppSettingApiService);

  private readonly citiesByProvince = signal<Record<number, City[]>>({});
  private readonly loadingProvinceIds = signal<ReadonlySet<number>>(new Set());

  private readonly loadedProvinceIds = new Set<number>();
  private readonly inflightByProvince = new Map<number, Observable<boolean>>();

  readonly citiesByProvinceMap = this.citiesByProvince.asReadonly();

  cities(provinceId: number): City[] | null {
    return this.citiesByProvince()[provinceId] ?? null;
  }

  isLoading(provinceId: number): boolean {
    return this.loadingProvinceIds().has(provinceId);
  }

  getCityName(provinceId: number, cityId: number): string | null {
    return this.cities(provinceId)?.find(city => city.id === cityId)?.name ?? null;
  }

  ensureLoaded(provinceId: number, force = false): Observable<boolean> {
    if (!force && this.loadedProvinceIds.has(provinceId) && this.cities(provinceId) != null) {
      return of(true);
    }

    const inflight = this.inflightByProvince.get(provinceId);

    if (inflight) {
      return inflight;
    }

    this.setLoading(provinceId, true);

    const request$ = this.api.getCities(provinceId).pipe(
      filter(response => response.success),
      map(response => response.result),
      tap(cities => this.setData(provinceId, cities)),
      map(() => true),
      catchError(() => of(false)),
      finalize(() => {
        this.setLoading(provinceId, false);
        this.inflightByProvince.delete(provinceId);
      }),
      shareReplay(1),
    );

    this.inflightByProvince.set(provinceId, request$);

    return request$;
  }

  refresh(provinceId: number): Observable<boolean> {
    return this.ensureLoaded(provinceId, true);
  }

  setData(provinceId: number, cities: City[]): void {
    this.citiesByProvince.update(current => ({
      ...current,
      [provinceId]: cities,
    }));
    this.loadedProvinceIds.add(provinceId);
  }

  clear(): void {
    this.citiesByProvince.set({});
    this.loadedProvinceIds.clear();
    this.inflightByProvince.clear();
    this.loadingProvinceIds.set(new Set());
  }

  private setLoading(provinceId: number, loading: boolean): void {
    this.loadingProvinceIds.update(current => {
      const next = new Set(current);

      if (loading) {
        next.add(provinceId);
      } else {
        next.delete(provinceId);
      }

      return next;
    });
  }
}
